import React, { useEffect, useState, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import * as Sentry from '@sentry/nextjs'
import { motion, AnimatePresence } from 'framer-motion'
import { MagnifyingGlassIcon, HomeIcon, ChevronRightIcon, StarIcon } from "@radix-ui/react-icons"

import Header from '../components/layouts/header'
import { Seo } from '@/components/seo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import Link from 'next/link'
import { useDevtools, Tool } from '@/hooks/useDevtool'
import { useLocalStorage } from 'react-use'

function NotFoundPage() {
  const t = useTranslations('NotFound')
  const indexT = useTranslations('Index')
  const router = useRouter()
  const currentPath = router.asPath
  const allTools = useDevtools()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [starTools] = useLocalStorage<Tool[]>('Home:Tools:ME', [])

  // 随机推荐工具
  const randomTools = useMemo(() => {
    const shuffled = [...allTools].sort(() => 0.5 - Math.random())
    return shuffled.slice(0, 6)
  }, [allTools])

  // 热门工具（基于收藏或特定标签）
  const popularTools = useMemo(() => {
    return allTools.filter(tool => 
      tool.labels.includes('top') || 
      tool.labels.includes('dev') ||
      tool.labels.includes('color')
    ).slice(0, 6)
  }, [allTools])

  // 搜索结果
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    return allTools.filter(tool => 
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      tool.description.toLowerCase().includes(searchQuery.toLowerCase())
    ).slice(0, 6)
  }, [allTools, searchQuery])

  useEffect(() => {
    Sentry.captureMessage(`404 - Page Not Found: ${currentPath}`, 'warning')
  }, [currentPath])

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/?search=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Seo
        title={t('pageTitle')}
        description={t('pageDescription')}
        path="/404"
      />
      <Header title={t('headerTitle')} />
      
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-4xl mx-auto"
        >
          {/* 404 主体区域 */}
          <motion.div 
            variants={itemVariants}
            className="text-center mb-12"
          >
            <div className="relative mb-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.2
                }}
                className="inline-block"
              >
                <h1 className="text-8xl sm:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-600 relative">
                  404
                  <motion.div
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                </h1>
              </motion.div>
            </div>
            
            <motion.div variants={itemVariants} className="space-y-4 mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800">
                哎呀，页面不见了！🔍
              </h2>
              <p className="text-lg text-gray-600 max-w-md mx-auto">
                您要找的页面可能已经搬家了，或者从未存在过。别担心，让我们帮您找到想要的工具！
              </p>
            </motion.div>

            {/* 搜索框 */}
            <motion.div variants={itemVariants} className="max-w-md mx-auto mb-8">
              <form onSubmit={handleSearch} className="relative">
                <Input
                  type="text"
                  placeholder={indexT('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-3 text-lg border-2 border-orange-200 focus:border-orange-400 rounded-xl"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Button 
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  size="sm"
                >
                  搜索
                </Button>
              </form>
            </motion.div>

            {/* 快速导航按钮 */}
            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4 mb-12">
              <Link href="/">
                <Button size="lg" className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  <HomeIcon className="w-5 h-5 mr-2" />
                  返回首页
                </Button>
              </Link>
              <Link href="/?label=dev">
                <Button variant="outline" size="lg">
                  开发工具
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link href="/?label=color">
                <Button variant="outline" size="lg">
                  色彩工具
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* 搜索结果 */}
          <AnimatePresence>
            {searchQuery.trim() && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-12"
              >
                <Card className="border-2 border-green-200 bg-green-50/50">
                  <CardHeader>
                    <CardTitle className="text-green-800">
                      搜索结果："{searchQuery}"
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {searchResults.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {searchResults.map((tool, index) => (
                          <ToolCard key={tool.href} tool={tool} delay={index * 0.1} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-green-700">没有找到匹配的工具，试试其他关键词吧！</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 我的收藏 */}
          {starTools && starTools.length > 0 && (
            <motion.div variants={itemVariants} className="mb-12">
              <Card className="border-2 border-yellow-200 bg-yellow-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center text-yellow-800">
                    <StarIcon className="w-5 h-5 mr-2 text-yellow-600" />
                    我的收藏工具
                  </CardTitle>
                  <CardDescription>
                    您收藏的工具，快速访问最常用的功能
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {starTools.slice(0, 6).map((tool, index) => (
                      <ToolCard key={tool.href} tool={tool} delay={index * 0.1} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* 热门推荐 */}
          <motion.div variants={itemVariants} className="mb-12">
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-blue-800">🔥 热门工具推荐</CardTitle>
                <CardDescription>
                  最受欢迎的开发工具，说不定有您需要的
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {popularTools.map((tool, index) => (
                    <ToolCard key={tool.href} tool={tool} delay={index * 0.1} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* 随机发现 */}
          <motion.div variants={itemVariants} className="mb-12">
            <Card className="border-2 border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-purple-800">✨ 随机发现</CardTitle>
                <CardDescription>
                  发现一些您可能感兴趣的工具
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {randomTools.map((tool, index) => (
                    <ToolCard key={tool.href} tool={tool} delay={index * 0.1} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>


        </motion.div>
      </div>
    </div>
  )
}

// 工具卡片组件
function ToolCard({ tool, delay = 0 }: { tool: Tool, delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      className="group"
    >
      <Link href={tool.href}>
        <Card className="h-full cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-orange-400 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                {tool.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors duration-200 truncate">
                  {tool.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {tool.description}
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {tool.labels.slice(0, 2).map((label) => (
                    <Badge key={label} variant="secondary" className="text-xs">
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  )
}

export default NotFoundPage

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}
